'use client';

import {
  type HTMLAttributes,
  type HTMLProps,
  type ReactElement,
  type ReactNode,
  type Ref,
  cloneElement,
  createContext,
  isValidElement,
  useContext,
  useRef,
  useState,
} from 'react';

import {
  FloatingArrow,
  FloatingPortal,
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useMergeRefs,
  useRole,
  useTransitionStyles,
} from '@floating-ui/react';
import classnames from 'classnames';

import { useHoverCapability } from '../../utils/media';
import type { BaseComponentProps } from '../base-component.types';
import { Button } from '../button';
import type {
  TooltipContentProps,
  TooltipProps,
  TooltipTriggerProps,
} from './Tooltip.types';

import styles from './Tooltip.module.scss';

export function useTooltip({
  initialOpen = false,
  placement = 'top',
  strategy,
  delay = 0,
  dismissOnAncestorScroll = false,
  mouseOnly = false,
  open: controlledOpen,
  onOpenChange: setControlledOpen,
}: TooltipProps = {}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(initialOpen);

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;

  const arrowRef = useRef(null);

  const data = useFloating({
    placement,
    strategy,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(15),
      flip({
        crossAxis: placement.includes('-'),
        fallbackAxisSideDirection: 'start',
        padding: 15,
      }),
      shift({ padding: 15 }),
      arrow({
        element: arrowRef,
      }),
    ],
  });

  const context = data.context;

  const hasHover = useHoverCapability();
  const hoverEnabled = controlledOpen == null && (!mouseOnly || hasHover);

  const hover = useHover(context, {
    move: false,
    mouseOnly,
    enabled: hoverEnabled,
    restMs: delay,
  });
  const focus = useFocus(context, {
    enabled: controlledOpen == null,
  });
  const dismiss = useDismiss(context, {
    outsidePress: true,
    ancestorScroll: dismissOnAncestorScroll,
  });
  const role = useRole(context, { role: 'tooltip' });

  const interactions = useInteractions([hover, focus, dismiss, role]);

  return {
    open,
    setOpen,
    ...interactions,
    ...data,
    arrowRef,
  };
}

type ContextType = ReturnType<typeof useTooltip> | null;

const TooltipContext = createContext<ContextType>(null);

export const useTooltipContext = () => {
  const context = useContext(TooltipContext);

  if (context == null) {
    throw new Error('Tooltip components must be wrapped in <Tooltip />');
  }

  return context;
};

export function Tooltip({
  children,
  ...options
}: { children: ReactNode } & TooltipProps) {
  const tooltip = useTooltip(options);

  return (
    <TooltipContext.Provider value={tooltip}>
      {children}
    </TooltipContext.Provider>
  );
}

export function TooltipTrigger({
  children,
  asChild = false,
  testId = 'ui-tooltip-trigger',
  ref: propRef,
  ...props
}: HTMLProps<HTMLElement> & TooltipTriggerProps & { ref?: Ref<HTMLElement> }) {
  const context = useTooltipContext();

  const childRef =
    asChild && isValidElement(children)
      ? (children.props as { ref?: Ref<HTMLElement> }).ref
      : null;

  const ref = useMergeRefs([
    context.refs.setReference,
    propRef,
    childRef ?? null,
  ]);

  // `asChild` allows the user to pass any element as the anchor
  if (asChild && isValidElement(children)) {
    const child = children as ReactElement;
    const childProps = child.props as HTMLAttributes<HTMLElement>;

    return cloneElement(child, {
      // eslint-disable-next-line react-hooks/refs
      ...context.getReferenceProps({
        ...(props as HTMLAttributes<HTMLElement>),
        ...childProps,
        ref,
        'data-state': context.open ? 'open' : 'closed',
        'data-testid': testId,
      } as HTMLAttributes<HTMLElement>),
    });
  }

  return (
    <Button
      ref={ref}
      data-state={context.open ? 'open' : 'closed'}
      data-testid={testId}
      {...context.getReferenceProps(props)}
    >
      {children}
    </Button>
  );
}

export function TooltipContent({
  style,
  className,
  flush,
  testId = 'ui-tooltip-content',
  ref: propRef,
  ...props
}: HTMLProps<HTMLDivElement> &
  BaseComponentProps &
  TooltipContentProps & { ref?: Ref<HTMLDivElement> }) {
  const { context: floatingContext, ...context } = useTooltipContext();
  const ref = useMergeRefs([context.refs.setFloating, propRef]);

  const { isMounted, styles: transitionStyles } = useTransitionStyles(
    floatingContext,
    {
      duration: {
        open: 200,
        close: 100,
      },
      open: {
        animationName: 'fadeIn',
        animationDuration: '0.2s',
        animationTimingFunction: 'ease',
        animationFillMode: 'forwards',
      },
      close: {
        animationName: 'fadeOut',
        animationDuration: '0.1s',
        animationTimingFunction: 'ease',
        animationFillMode: 'forwards',
      },
    },
  );

  if (!isMounted) return null;

  return (
    <FloatingPortal>
      <div
        ref={ref}
        style={{
          ...context.floatingStyles,
          ...transitionStyles,
        }}
        className={styles['tooltip']}
        data-testid={testId}
        {...context.getFloatingProps(props)}
      >
        <div
          style={{
            ...style,
          }}
          className={classnames(
            styles['content'],
            flush && styles['content--flush'],
            className,
          )}
        >
          {props.children}
        </div>
        <FloatingArrow
          ref={context.arrowRef}
          context={floatingContext}
          className={styles['arrow']}
        />
      </div>
    </FloatingPortal>
  );
}
